import { Children, ElementType, ReactNode, isValidElement } from 'react';
import { isFragment } from 'react-is';

type Slots = Record<string, ElementType>;
const getSlotByComponent = (slots: Slots, component: ReactNode) =>
  Object.keys(slots).find(
    (key) => isValidElement(component) && slots[key] === component.type,
  );

/**
 * Converts children to a map of defined slots
 * @example
 * const { component: componentReactNodes, rest: restOfChildrenNodes } = getChildrenBySlots(children, {slotName: Component});
 *
 * @example
 * Component.slot = 'component'
 * const { component: componentReactNodes, rest: restOfChildrenNodes } = getChildrenBySlots(children);
 */
export const getChildrenBySlots = (
  children: ReactNode | ReactNode[],
  slots?: Slots,
) => {
  let normalizedChildren: ReturnType<typeof Children.toArray>;

  if (children instanceof Array) {
    normalizedChildren = children
      .map((child) => {
        if (isFragment(child)) {
          return child.props.children;
        }
        return child;
      })
      .flat();
  } else {
    normalizedChildren = Children.toArray(children);
  }

  const accumulator: Record<string, ReactNode[]> = { rest: [] };

  const result = normalizedChildren.reduce((slottedResults, child) => {
    let slot = getSlotByComponent(slots ?? {}, child);
    slot =
      !slot && isValidElement(child)
        ? ((child.type as any)?.slot as string)
        : slot;

    if (slot) {
      return {
        ...slottedResults,
        [slot]: [...(slottedResults[slot] ?? []), child],
      };
    }
    return {
      ...slottedResults,
      rest: [...slottedResults.rest, child],
    };
  }, accumulator);
  return result as Record<string, ReactNode>;
};
